import { useResetStoreData } from 'features/request/useResetStoreData';
import type { InputMaybe } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IDatasource, IGetRowsParams } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { MutableRefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { usePreviousValue } from './usePrevious';

type CustomFnType<T> = (params: IGetRowsParams) => InputMaybe<T | T[]> | undefined;

type TAgGridDataSourceProps<T, S> = {
    requestId: string;
    requestFn: (variables: T) => PayloadAction;
    requestVariables: T;
    takeRowsParam?: number;
    customSortFn?: CustomFnType<S>;
    customWhereFn?: CustomFnType<S>;
    gridTableRef?: AgGridReact | null;
    onRowsLoaded?: (params: IGetRowsParams) => boolean;
};

type TAgGridDataSourceResult = {
    dataSource: IDatasource;
    getRowParams: MutableRefObject<IGetRowsParams | undefined>;
};

export const useAgGridDataSource = <T = any, S = any>(props: TAgGridDataSourceProps<T, S>): TAgGridDataSourceResult => {
    const dispatch = useDispatch();

    const {
        requestId,
        requestFn,
        requestVariables,
        takeRowsParam = 50,
        customWhereFn,
        customSortFn,
        gridTableRef,
        onRowsLoaded,
    } = props;

    const retryRequestParams = useRef<AnyObject | null>(null);
    const getRowParamsRef = useRef<IGetRowsParams>();

    const prevVariables = usePreviousValue(requestVariables);

    const [resetData] = useResetStoreData({ requestId });

    const dataSource = useMemo(
        () => ({
            rowCount: undefined,

            // eslint-disable-next-line @typescript-eslint/no-empty-function
            getRows: (params: IGetRowsParams) => {},
            destroy() {
                resetData(null);
            },
        }),
        [resetData],
    );

    useEffect(() => {
        dataSource.getRows = (params: IGetRowsParams) => {
            const currentRequestParams = {
                take: takeRowsParam,
                skip: params.startRow,
                ...(customSortFn ? { order: customSortFn(params) } : {}),
                ...(customWhereFn ? { where: customWhereFn(params) } : {}),
            };

            if (requestVariables) {
                dispatch(
                    requestFn({
                        ...requestVariables,
                        ...currentRequestParams,
                    }),
                );
            }

            if (gridTableRef || onRowsLoaded) {
                const successCallbackRef = params.successCallback;

                params.successCallback = (rowsThisBlock: any[], lastRow?: number) => {
                    const cell = gridTableRef?.api.getFocusedCell();

                    successCallbackRef(rowsThisBlock, lastRow);

                    const isFocusedCellInLoadedBlock =
                        cell != null && cell.rowIndex >= params.startRow && cell.rowIndex < params.endRow;

                    if (!onRowsLoaded?.(params) && isFocusedCellInLoadedBlock) {
                        setTimeout(() => gridTableRef?.api?.setFocusedCell(cell.rowIndex, cell.column), 500);
                    }
                };
            }

            retryRequestParams.current = currentRequestParams;
            getRowParamsRef.current = params;
        };
    }, [
        customSortFn,
        customWhereFn,
        dataSource,
        dispatch,
        gridTableRef,
        onRowsLoaded,
        requestFn,
        requestVariables,
        takeRowsParam,
    ]);

    useEffect(() => {
        if (!prevVariables && requestVariables && !!retryRequestParams.current) {
            dispatch(requestFn({ ...requestVariables, ...retryRequestParams.current }));
            retryRequestParams.current = null;
        }
    }, [dispatch, prevVariables, requestFn, requestVariables]);

    return {
        dataSource,
        getRowParams: getRowParamsRef,
    };
};
